// src/pages/SubdomainManagementPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSubdomainNotifications, updateSubdomainNotificationStatus } from '../services/subdomainService';
import Header from '../components/Header';
import LoadingSpinner from '../components/LoadingSpinner';
// import '../styles/pages/SubdomainManagementPage.scss';

const SubdomainManagementPage = () => {
    const { currentUser, accountId, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState(null);

    useEffect(() => {
        if (authLoading || !currentUser) {
            if (!currentUser && !authLoading) {
                navigate('/login');
            }
            return;
        }

        fetchNotifications();
    }, [currentUser, authLoading, navigate]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const data = await getSubdomainNotifications();
            setNotifications(data);
        } catch (err) {
            console.error('通知の取得に失敗しました:', err);
            setError('通知の読み込み中にエラーが発生しました。');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (notificationId, status, adminNotes = '') => {
        try {
            setUpdating(notificationId);
            await updateSubdomainNotificationStatus(notificationId, status, adminNotes);

            // 通知リストを更新
            setNotifications(prev =>
                prev.map(notification =>
                    notification.id === notificationId
                        ? { ...notification, status, adminNotes, updatedAt: new Date() }
                        : notification
                )
            );

            alert(`ステータスを「${getStatusText(status)}」に更新しました。`);
        } catch (err) {
            console.error('ステータス更新に失敗しました:', err);
            alert('ステータスの更新に失敗しました。');
        } finally {
            setUpdating(null);
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pending': return '保留中';
            case 'approved': return '承認済み';
            case 'rejected': return '却下';
            default: return status;
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'pending': return 'status-pending';
            case 'approved': return 'status-approved';
            case 'rejected': return 'status-rejected';
            default: return '';
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '未設定';
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            return timestamp.toDate().toLocaleString('ja-JP');
        }
        if (timestamp instanceof Date) {
            return timestamp.toLocaleString('ja-JP');
        }
        return '無効な日時';
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div>
                <Header />
                <main className="subdomain-management-container">
                    <p className="error-message">{error}</p>
                </main>
            </div>
        );
    }

    return (
        <div>
            <Header />
            <main className="subdomain-management-container">
                <div className="page-header">
                    <h1>サブドメイン管理</h1>
                    <p>ユーザーが登録したサブドメインの承認・却下を行います。</p>
                </div>

                {notifications.length === 0 ? (
                    <div className="empty-state">
                        <p>保留中のサブドメイン登録はありません。</p>
                    </div>
                ) : (
                    <div className="notifications-list">
                        {notifications.map((notification) => (
                            <div key={notification.id} className="notification-card">
                                <div className="notification-header">
                                    <h3>{notification.subdomain}.suisui.life</h3>
                                    <span className={`status-badge ${getStatusBadgeClass(notification.status)}`}>
                                        {getStatusText(notification.status)}
                                    </span>
                                </div>

                                <div className="notification-details">
                                    <p><strong>ユーザー:</strong> {notification.userEmail}</p>
                                    <p><strong>アカウントID:</strong> {notification.accountId}</p>
                                    <p><strong>登録日時:</strong> {formatTimestamp(notification.createdAt)}</p>
                                    <p><strong>更新日時:</strong> {formatTimestamp(notification.updatedAt)}</p>
                                    <p><strong>メッセージ:</strong> {notification.message}</p>

                                    {notification.adminNotes && (
                                        <p><strong>管理者メモ:</strong> {notification.adminNotes}</p>
                                    )}
                                </div>

                                {notification.status === 'pending' && (
                                    <div className="notification-actions">
                                        <button
                                            onClick={() => {
                                                const notes = prompt('承認メモ（オプション）:');
                                                handleStatusUpdate(notification.id, 'approved', notes);
                                            }}
                                            className="approve-button"
                                            disabled={updating === notification.id}
                                        >
                                            {updating === notification.id ? '処理中...' : '承認'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                const notes = prompt('却下理由（必須）:');
                                                if (notes && notes.trim()) {
                                                    handleStatusUpdate(notification.id, 'rejected', notes);
                                                } else {
                                                    alert('却下理由を入力してください。');
                                                }
                                            }}
                                            className="reject-button"
                                            disabled={updating === notification.id}
                                        >
                                            {updating === notification.id ? '処理中...' : '却下'}
                                        </button>
                                    </div>
                                )}

                                {notification.status === 'approved' && (
                                    <div className="firebase-instructions">
                                        <h4>Firebase Hosting設定手順:</h4>
                                        <ol>
                                            <li>Firebase Consoleにアクセス</li>
                                            <li>Hosting → カスタムドメイン → ドメインを追加</li>
                                            <li>{notification.subdomain}.suisui.life を入力</li>
                                            <li>DNS設定を確認して完了</li>
                                        </ol>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default SubdomainManagementPage; 